import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function LoginForm({ onLogin, onRegister, error, branding }) {
  const { t } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    farmName: '',
  });

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await onRegister(form);
      } else {
        await onLogin(form.email, form.password);
      }
    } catch {
      // error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 bg-gradient-to-br from-aif-50 to-white dark:from-gray-950 dark:to-gray-900">
      <div className="w-full max-w-sm">
        {/* Logo — tenant branding or default */}
        <div className="text-center mb-8">
          {branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.app_name || t('app.name')}
              className="h-16 mx-auto mb-4"
            />
          ) : (
            <div
              className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg ${branding?.primary_color ? '' : 'bg-aif-700 dark:bg-aif-600'}`}
              style={branding?.primary_color ? { backgroundColor: branding.primary_color } : undefined}
            >
              <span className="text-white font-bold text-3xl">
                {(branding?.app_name || 'A').charAt(0)}
              </span>
            </div>
          )}
          <h1 className="text-2xl font-bold text-[var(--aif-text-primary)]">
            {branding?.app_name || t('app.name')}
          </h1>
          <p className="text-sm text-[var(--aif-text-muted)] mt-1">
            {branding?.tagline || t('app.tagline')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-[var(--aif-bg-surface)] rounded-2xl shadow-lg border border-[var(--aif-border-default)] p-6 space-y-4">
          {isRegister && (
            <>
              <div>
                <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
                  {t('auth.name')}
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={update('name')}
                  required
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-[var(--aif-text-primary)] text-sm placeholder-[var(--aif-text-muted)] focus:outline-none focus:border-aif-500 focus-visible:ring-1 focus-visible:ring-[var(--aif-color-primary)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
                  {t('auth.farmName')}
                </label>
                <input
                  type="text"
                  value={form.farmName}
                  onChange={update('farmName')}
                  className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-[var(--aif-text-primary)] text-sm placeholder-[var(--aif-text-muted)] focus:outline-none focus:border-aif-500 focus-visible:ring-1 focus-visible:ring-[var(--aif-color-primary)]"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
              {t('auth.email')}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={update('email')}
              required
              autoComplete="email"
              className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-[var(--aif-text-primary)] text-sm placeholder-[var(--aif-text-muted)] focus:outline-none focus:border-aif-500 focus-visible:ring-1 focus-visible:ring-[var(--aif-color-primary)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--aif-text-secondary)] mb-1">
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={update('password')}
              required
              minLength={6}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              className="w-full px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-input)] text-[var(--aif-text-primary)] text-sm placeholder-[var(--aif-text-muted)] focus:outline-none focus:border-aif-500 focus-visible:ring-1 focus-visible:ring-[var(--aif-color-primary)]"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-aif-700 hover:bg-aif-800 dark:bg-aif-600 dark:hover:bg-aif-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
          >
            {loading ? '...' : isRegister ? t('auth.register') : t('auth.login')}
          </button>

          <p className="text-center text-sm text-[var(--aif-text-muted)] dark:text-gray-400">
            {isRegister ? t('auth.hasAccount') : t('auth.noAccount')}{' '}
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-aif-700 dark:text-aif-400 font-medium hover:underline"
            >
              {isRegister ? t('auth.login') : t('auth.register')}
            </button>
          </p>
        </form>

        {/* Legal footer links */}
        <div className="flex justify-center gap-4 mt-6 text-xs text-[var(--aif-text-tertiary)] dark:text-gray-500">
          <a href="/datenschutz" className="hover:text-gray-600 dark:hover:text-gray-300 transition">
            Datenschutz
          </a>
          <span>|</span>
          <a href="/agb" className="hover:text-gray-600 dark:hover:text-gray-300 transition">
            AGB
          </a>
          <span>|</span>
          <a href="/impressum" className="hover:text-gray-600 dark:hover:text-gray-300 transition">
            Impressum
          </a>
        </div>

        {branding?.powered_by_visible && (
          <p className="text-center text-xs text-[var(--aif-text-tertiary)] dark:text-gray-600 mt-4">
            Powered by AI Farm
          </p>
        )}
      </div>
    </div>
  );
}
